import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Action } from 'src/app/core/models/action.model';
import { UserService } from 'src/app/core/services/user.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { KycDataService } from '../../services/kyc-data.service';
import { KYCActionState } from 'src/app/core/models/kyc-validation.model';
import { KycStateService } from 'src/app/core/services/kyc-state.service';
import {
  LegalNameMismatchResolver,
  UniqueDocumentValueResolver,
} from 'src/app/core/services/kyc-resolvers';
import { EventService } from 'src/app/core/services/event.service';
import { EventTypes } from 'src/app/core/models/events.model';
import { ActivatedRoute } from '@angular/router';
import { notesAllowProceed, isAgentSkipCheck } from 'src/app/core/utils/kyc-postpone.util';

type GuideKycState = 'PENDING' | 'DONE' | 'FAILED';

@Component({
  selector: 'app-kyc',
  templateUrl: './kyc.component.html',
  styleUrls: ['./kyc.component.scss'],
})
export class KycComponent implements OnInit {
  @Input() action!: Action;

  @Output() completed = new EventEmitter();

  loaded = false;

  // Estado do componente no contexto do Guide
  currentState: GuideKycState = 'PENDING';

  // Estado original do KYC (do KycStateService)
  kycState: KYCActionState = 'LOADING';

  // Estados de UI
  showProvider = false;

  constructor(
    private userService: UserService,
    private kycDataService: KycDataService,
    private kycStateService: KycStateService,
    private kycService: KycService,
    private dialog: MatDialog,
    private eventService: EventService,
    private activatedRoute: ActivatedRoute
  ) {
    // Registrar resolvers
    this.registerResolvers();
  }

  /**
   * Registra os resolvers de validação para este componente
   */
  private registerResolvers(): void {
    this.kycStateService.registerResolver(
      new LegalNameMismatchResolver(this.dialog, this.kycService)
    );
    this.kycStateService.registerResolver(new UniqueDocumentValueResolver());
  }

  async ngOnInit(): Promise<void> {
    console.log('[GUIDE_KYC] Component initialized', {
      actionId: this.action.action_id,
      actionType: this.action.action_type,
      currentStatus: this.action.ac_status,
    });

    await this.checkInitialState();
  }

  // ngOnDestroy não é mais necessário pois não há polling

  /**
   * Verifica o estado inicial da action e decide o que fazer
   */
  private async checkInitialState(): Promise<void> {
    const status = this.action.ac_status;

    console.log('[GUIDE_KYC] Checking initial state', {
      actionId: this.action.action_id,
      status,
      doneActionId: this.action.done_action_id,
    });

    switch (status) {
      case 'DONE':
        // ⚠️ KYC tem status DONE, mas precisa validar se passed = true
        console.log('[GUIDE_KYC] Status is DONE, validating KYC passed field', {
          actionId: this.action.action_id,
          doneActionId: this.action.done_action_id,
        });

        // Usa o KycStateService que já valida o campo passed internamente
        const kycCheckResult = await this.kycStateService.checkState(this.action);

        console.log('[GUIDE_KYC] KYC validation result', {
          actionId: this.action.action_id,
          kycState: kycCheckResult,
        });

        if (kycCheckResult === 'DONE_OK') {
          // ✅ KYC retornou DONE_OK, mas precisa VALIDAR o campo passed antes de emitir completed
          console.log('[GUIDE_KYC] DONE_OK state, validating passed field', {
            actionId: this.action.action_id,
            doneActionId: this.action.done_action_id,
          });

          // Se não tiver done_action_id, trata como falha
          if (!this.action.done_action_id) {
            console.warn('[GUIDE_KYC] DONE_OK but no done_action_id, treating as FAIL', {
              actionId: this.action.action_id,
            });
            await this.checkStateAndProceed();
            break;
          }

          try {
            // Busca o kyc_check para validar o campo passed
            const kycCheck = await this.kycStateService.getKycCheck(this.action.done_action_id);
            
            console.log('[GUIDE_KYC] KYC check data retrieved', {
              actionId: this.action.action_id,
              kycCheckId: kycCheck?.id,
              passed: kycCheck?.passed,
              hasNotes: !!kycCheck?.notes,
              notesCount: kycCheck?.notes?.length || 0,
            });

            // Check if KYC was bypassed by an agent
            const currentUser = await this.userService.getCurrentUser();
            const bypassedByAgent = !!(currentUser?.attributes as any)?.kyc?.bypassedByAgent;

            // ✅ Se passed = true, bypassed por agente, adiável (postpone CPK-3620),
            // OU skipped por agente (CPK-4051), emite completed
            const canProceed = notesAllowProceed((kycCheck as any)?.notes);
            const agentSkip = isAgentSkipCheck(kycCheck as any);
            if (kycCheck?.passed === true || bypassedByAgent || canProceed || agentSkip) {
              console.log('[GUIDE_KYC] KYC passed or bypassed, emitting completed', {
                actionId: this.action.action_id,
                kycCheckId: kycCheck?.id,
                passed: kycCheck?.passed,
                bypassedByAgent,
              });
              this.currentState = 'DONE';
              this.loaded = true;
              this.completed.emit();
            } else {
              // ❌ Se passed = false OU null/undefined, trata como falha
              console.warn('[GUIDE_KYC] KYC passed = false or null, showing provider with error', {
                actionId: this.action.action_id,
                kycCheckId: kycCheck?.id,
                passed: kycCheck?.passed,
                hasNotes: !!kycCheck?.notes,
              });
              // Mostra o provider que exibirá o erro baseado nos notes
              await this.checkStateAndProceed();
            }
          } catch (error) {
            console.error('[GUIDE_KYC] Error fetching kyc_check, treating as FAIL', {
              actionId: this.action.action_id,
              doneActionId: this.action.done_action_id,
              error,
            });
            await this.checkStateAndProceed();
          }
        } else if (kycCheckResult === 'DONE_FAIL') {
          // ❌ KYC falhou - tenta resolver
          console.log('[GUIDE_KYC] KYC failed validation, attempting to resolve', {
            actionId: this.action.action_id,
          });
          await this.checkStateAndProceed();
        } else {
          // ⏳ Outro estado - trata normalmente
          console.log('[GUIDE_KYC] Unexpected state for DONE status, checking state', {
            actionId: this.action.action_id,
            kycState: kycCheckResult,
          });
          await this.checkStateAndProceed();
        }
        break;

      case 'PROCESSING':
      case 'SUBMITTED':
        // ⏳ Em processamento - mostra provider (ele fará o polling)
        console.log('[GUIDE_KYC] In processing, showing provider', {
          actionId: this.action.action_id,
          status,
        });
        await this.checkStateAndProceed();
        break;

      case 'PENDING':
      case 'FAILED':
      case null:
      case undefined:
        // ⚠️ Pendente ou falhou - verifica com KycStateService
        // FAILED significa que stage = DONE mas passed = false (detectado pelo ManagementStepService)
        console.log('[GUIDE_KYC] Pending or failed, checking with KycStateService', {
          actionId: this.action.action_id,
          status,
          reason: status === 'FAILED' ? 'KYC completed but failed (passed = false)' : 'KYC not started or pending',
        });
        await this.checkStateAndProceed();
        break;

      default:
        // Estado desconhecido - trata como pendente
        console.warn('[GUIDE_KYC] Unknown status, treating as pending', {
          actionId: this.action.action_id,
          status,
        });
        await this.checkStateAndProceed();
    }
  }

  /**
   * Verifica o estado do KYC usando KycStateService (lógica legada)
   */
  private async checkStateAndProceed(): Promise<void> {
    console.log('[GUIDE_KYC] Checking state with KycStateService', {
      actionId: this.action.action_id,
      ac_status: this.action.ac_status,
      done_action_id: this.action.done_action_id,
    });

    // Verificar estado atual
    this.kycState = await this.kycStateService.checkState(this.action);
    console.log('[GUIDE_KYC] KycStateService returned state', {
      actionId: this.action.action_id,
      kycState: this.kycState,
      ac_status: this.action.ac_status,
    });

    // Decidir ação baseado no estado
    switch (this.kycState) {
      case 'DONE_OK':
        // ✅ KYC retornou DONE_OK, MAS precisa validar passed antes de emitir completed
        console.log('[GUIDE_KYC] DONE_OK state, validating passed field before completing', {
          actionId: this.action.action_id,
          done_action_id: this.action.done_action_id,
        });

        // Se não tiver done_action_id, não pode validar - mostra provider
        if (!this.action.done_action_id) {
          console.warn('[GUIDE_KYC] DONE_OK but no done_action_id, showing provider', {
            actionId: this.action.action_id,
          });
          this.currentState = 'FAILED';
          await this.buildComponent();
          this.loaded = true;
          break;
        }

        try {
          // ⚠️ CRITICAL: Valida o campo passed mesmo com DONE_OK
          const kycCheck = await this.kycStateService.getKycCheck(this.action.done_action_id);
          
          console.log('[GUIDE_KYC] Double-checking passed field', {
            actionId: this.action.action_id,
            kycCheckId: kycCheck?.id,
            passed: kycCheck?.passed,
            stage: kycCheck?.stage,
          });

          // Check if KYC was bypassed by an agent
          const user = await this.userService.getCurrentUser();
          const bypassed = !!(user?.attributes as any)?.kyc?.bypassedByAgent;

          // ✅ Emite completed se passed = true, bypassed, OU adiável (postpone CPK-3620)
          const canProceed = notesAllowProceed((kycCheck as any)?.notes);
          if (kycCheck?.passed === true || bypassed || canProceed) {
            console.log('[GUIDE_KYC] Passed or bypassed confirmed, emitting completed', {
              actionId: this.action.action_id,
              passed: kycCheck?.passed,
              bypassedByAgent: bypassed,
            });
            this.currentState = 'DONE';
            this.loaded = true;
            this.completed.emit();
          } else {
            // ❌ Se passed = false/null e não bypassed, mostra provider com erro
            console.error('[GUIDE_KYC] CRITICAL: KycStateService returned DONE_OK but passed is not true!', {
              actionId: this.action.action_id,
              kycCheckId: kycCheck?.id,
              passed: kycCheck?.passed,
              kycState: this.kycState,
              bypassedByAgent: bypassed,
            });
            this.currentState = 'FAILED';
            await this.buildComponent();
            this.loaded = true;
          }
        } catch (error) {
          console.error('[GUIDE_KYC] Error validating passed field, showing provider', {
            actionId: this.action.action_id,
            error,
          });
          this.currentState = 'FAILED';
          await this.buildComponent();
          this.loaded = true;
        }
        break;

      case 'IN_PROGRESS':
        // KYC em processamento - mostra mensagem de espera no provider
        console.log('[GUIDE_KYC] IN_PROGRESS - showing provider', {
          actionId: this.action.action_id,
        });
        this.currentState = 'PENDING';
        await this.buildComponent();
        this.loaded = true;
        break;

      case 'BLOCKED':
        // Action anterior não completado - mostra provider que exibirá o erro
        console.log('[GUIDE_KYC] BLOCKED - showing provider', {
          actionId: this.action.action_id,
        });
        this.currentState = 'FAILED';
        await this.buildComponent();
        this.loaded = true;
        break;

      case 'DONE_FAIL':
        // KYC falhou - mostra provider que já tem o erro nos notes
        console.log('[GUIDE_KYC] DONE_FAIL - showing provider with error', {
          actionId: this.action.action_id,
        });
        this.currentState = 'FAILED';
        await this.buildComponent();
        break;

      case 'PENDING':
      default:
        // KYC pendente - mostra provider para iniciar
        console.log('[GUIDE_KYC] PENDING - showing provider', {
          actionId: this.action.action_id,
        });
        this.currentState = 'PENDING';
        await this.buildComponent();
        break;
    }
  }



  async buildComponent() {
    console.log('[GUIDE_KYC] Building KYC provider component', {
      actionId: this.action.action_id,
      done_action_id: this.action.done_action_id,
      currentState: this.currentState,
    });

    const uid = (await this.userService.getCurrentUser())?.id;

    if (!this.action) {
      console.error('[GUIDE_KYC] No action loaded');
      throw new Error('No requirement loaded');
    }

    if (!uid) {
      console.error('[GUIDE_KYC] No user logged in');
      throw new Error('No user logged in');
    }

    // Busca o último KYC check do usuário para esta action
    // Isso permite que erros (como PEP/Sanctions) sejam detectados mesmo sem done_action_id
    let notes: any[] | undefined;
    let providerReturn: any | undefined;
    
    try {
      let kycCheck;
      
      if (this.action.done_action_id) {
        // Se tiver done_action_id, usa direto
        console.log('[GUIDE_KYC] Fetching KYC check by done_action_id', {
          actionId: this.action.action_id,
          doneActionId: this.action.done_action_id,
        });
        kycCheck = await this.kycStateService.getKycCheck(this.action.done_action_id);
      } else {
        // Se não tiver done_action_id, busca o último check do usuário para esta action
        console.log('[GUIDE_KYC] No done_action_id, fetching last check by user and action', {
          actionId: this.action.action_id,
          userId: uid,
        });
        
        const { data: lastCheck } = await (this.kycService.supabase
          .from('cp_kyc_check') as any)
          .select('*')
          .eq('checked_user_id', uid)
          .eq('action_id', this.action.action_id)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();
        
        kycCheck = lastCheck;
      }
      
      if (kycCheck) {
        notes = kycCheck.notes;
        providerReturn = kycCheck.provider_return;
        console.log('[GUIDE_KYC] Retrieved KYC check data', {
          actionId: this.action.action_id,
          kycCheckId: kycCheck.id,
          currentState: this.currentState,
          hasNotes: !!notes,
          notesCount: notes?.length || 0,
          hasProviderReturn: !!providerReturn,
          passed: kycCheck.passed,
          stage: kycCheck.stage,
          // Log últimos notes para debug de erros
          lastNotes: notes?.slice(-2).map((n: any) => ({
            message: n.message,
            date: n.date,
            data: n.data,
          })),
        });
      } else {
        console.log('[GUIDE_KYC] No previous KYC check found', {
          actionId: this.action.action_id,
          userId: uid,
        });
      }
    } catch (error) {
      console.error('[GUIDE_KYC] Error fetching KYC check data:', error);
    }

    this.kycDataService.setData({
      bucket: 'client-documents',
      name: 'kyc.png',
      multiple: false,
      ref: uid,
      type: 'PERSONAL',
      provider: this.action.config.provider,
      isComponentView: true,
      action_id: this.action.action_id || undefined,
      notes, // Passa os notes para o provider processar
      provider_return: providerReturn, // Passa o provider_return para detecção de erros
    });

    this.showProvider = true;
    this.loaded = true;

    console.log('[GUIDE_KYC] Provider component ready', {
      actionId: this.action.action_id,
      provider: this.action.config.provider,
      hasNotes: !!notes,
      hasProviderReturn: !!providerReturn,
    });

    await this.sendEvent();
  }

  /**
   * Chamado quando usuário completa o KYC no provider
   * Agora o polling é feito dentro do provider, então só emite completed
   */
  onComplete() {
    console.log('[GUIDE_KYC] User completed KYC provider', {
      actionId: this.action.action_id,
    });


    // O provider já fez o polling e verificação, só emite completed
    this.completed.emit();
  }

  private async sendEvent(): Promise<any> {

    const claimId = this.activatedRoute.snapshot.paramMap.get('claimId');
    const eventTypeValue: EventTypes = EventTypes.KYC_DOC_SELECTION_LOAD_PAGE;

    try{
      await this.eventService.sendEventWithCaseId(eventTypeValue, claimId);
    }catch(error) {
      console.error('Event not sended, ', error);
    }

  }

}
